import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';
import cx from 'classnames';
import { Text, Title } from '@dropbox/dig-components/typography';
import { Button } from '@dropbox/dig-components/buttons';
import { TextInput } from '@dropbox/dig-components/text_fields';
import {
  FormHelperText,
  FormLabel,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { UIIcon } from '@dropbox/dig-icons';
import { FailLine } from '@dropbox/dig-icons/assets';

import { getBrandName } from 'signer-app/utils/brand';
import { useAuthentication } from 'signer-app/authentication/use-authentication';
import { useArkoseIntercept } from 'signer-app/authentication/use-arkose-intercept';
import styles from 'signer-app/pages/confirmation-page/components/dropbox-sign-upsell.module.css';

const messages = defineMessages({
  title: {
    id: 'b5425337de53c824700efdd3b900ec271f194ea7c9b14dbca5ad000d533b4163',
    description: 'Dropbox Basic upsell title',
    defaultMessage: 'Get your own documents signed for free!',
  },
  blurb: {
    id: 'c8427f34d3df64627d4046d99aca1140169771e8987849d8f458486b0e64010b',
    description: 'Dropbox Basic upsell text below title',
    defaultMessage:
      'Sign up for our free {brandName} plan. Get unlimited self-signing + 3 signature requests every month.',
  },
  cta: {
    id: '105cc9625e5f09c766b57d229b24c79c5117482c0b45417031870ff60618f267',
    description: 'Dropbox Sign upsell call-to-action button text', // eslint-disable-line no-restricted-syntax
    defaultMessage: 'Sign up',
  },
  emailPlaceholder: {
    id: '19deaca7d495df79694d5897d46d499cecfecc5eb88fcc5605ffa9dc46db09e7',
    description: 'Placeholder text for email input',
    defaultMessage: 'email@example.com',
  },
  inputLabel: {
    id: '7d1d33a169f46b811a12462a76d011d003fe5119e2bc097588c3e33d76521f34',
    description: 'Aria label for email input',
    defaultMessage: 'Email address',
  },
  iconAriaLabel: {
    id: '8c5fbdbbd4fd6eab8727523381d3379e0fb23e05a50940d58924ac9a3eb4ba40',
    description: 'Aria label for error icon',
    defaultMessage: 'Error',
  },
  errorMessage: {
    id: '70a806c521a5ca8df757f503acbf7ef1cdef88b52096e2be9ed594e315fa5651',
    description: 'Error message for email input',
    defaultMessage: 'Please enter a valid email address',
  },
});

const yupEmail = Yup.string().email().required();

export function DropboxSignUpsell() {
  const intl = useIntl();
  const { state, update, meta, signUp } = useAuthentication();
  const [isEmailInvalid, setIsEmailInvalid] = React.useState(false);

  const arkoseSubmit = useArkoseIntercept(
    ({ arkoseToken }) => signUp({ arkoseToken }),
    state.email,
    meta?.arkoseEnabled,
    meta?.arkoseSignUpPublicKey,
  );

  const onFormSubmit = React.useCallback(
    (e) => {
      e.preventDefault();
      if (!state.email.length || !yupEmail.isValidSync(state.email)) {
        setIsEmailInvalid(true);
        return;
      }
      if (meta) {
        meta.redirectUrl = '/account/emailActivation';
      }
      return arkoseSubmit();
    },
    [arkoseSubmit, state.email, meta],
  );

  if (!meta) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Title className={styles.title}>
        {intl.formatMessage(messages.title)}
      </Title>
      <Text tagName="p" size="large">
        {intl.formatMessage(messages.blurb, {
          brandName: getBrandName('S'),
        })}
      </Text>
      <form className={styles.form} onSubmit={onFormSubmit}>
        <div className={styles.formRowContainer}>
          <FormRow>
            <FormLabel htmlFor="email">
              {intl.formatMessage(messages.inputLabel)}
            </FormLabel>
            <TextInput
              name="email"
              id="email"
              onChange={(e) => {
                setIsEmailInvalid(false);
                update('email', e.target.value);
              }}
              value={state.email}
              placeholder={intl.formatMessage(messages.emailPlaceholder)}
              aria-errormessage="email-helper-text"
              aria-describedby="email-error"
            />
            {isEmailInvalid && (
              <FormHelperText id="email-error" variant="alert">
                <UIIcon
                  src={FailLine}
                  size="small"
                  aria-label={intl.formatMessage(messages.iconAriaLabel)}
                />
                {intl.formatMessage(messages.errorMessage)}
              </FormHelperText>
            )}
            {state.error && !isEmailInvalid && (
              <FormHelperText id="email-error" variant="alert">
                <UIIcon
                  src={FailLine}
                  size="small"
                  aria-label={intl.formatMessage(messages.iconAriaLabel)}
                />
                {state.error.message}
              </FormHelperText>
            )}
          </FormRow>
        </div>
        <Button
          className={cx(styles.submit, {
            [styles.submitWhenError]: isEmailInvalid,
          })}
          type="submit"
          variant="primary"
        >
          {intl.formatMessage(messages.cta)}
        </Button>
      </form>
    </div>
  );
}
